import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { 
  Truck, MapPin, Box, ArrowRight, ArrowLeft, CheckCircle,
  Sofa, BedDouble, UtensilsCrossed, Refrigerator, Tv, Piano,
  Dumbbell, Package, Plus, Minus, Users, Home, Building, Warehouse,
  Loader2, Navigation
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Slider } from "@/components/ui/slider";
import { Checkbox } from "@/components/ui/checkbox";
import AddressAutocomplete from "@/components/AddressAutocomplete";
import VideoHero from "@/components/VideoHero";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Home size options
const HOME_SIZES = [
  { value: "studio", label: "Studio", hours: "~1.5 hrs" },
  { value: "1_bedroom", label: "1 Bedroom", hours: "~2 hrs" },
  { value: "2_bedroom", label: "2 Bedroom", hours: "~3 hrs" },
  { value: "3_bedroom", label: "3 Bedroom", hours: "~4 hrs" },
  { value: "4_bedroom", label: "4 Bedroom", hours: "~5 hrs" },
  { value: "5_bedroom", label: "5+ Bedroom", hours: "~6 hrs" },
];

const PROPERTY_TYPES = [
  { value: "apartment", label: "Apartment", icon: Building },
  { value: "condo", label: "Condo", icon: Building },
  { value: "house", label: "House", icon: Home },
  { value: "townhouse", label: "Townhouse", icon: Home },
  { value: "storage", label: "Storage", icon: Warehouse },
];

// Item categories and definitions
const REGULAR_ITEMS = [
  { id: "couch", name: "Couch/Sofa", icon: Sofa },
  { id: "loveseat", name: "Loveseat", icon: Sofa },
  { id: "armchair", name: "Armchair", icon: Sofa },
  { id: "bed_king", name: "Bed (King)", icon: BedDouble },
  { id: "bed_queen", name: "Bed (Queen)", icon: BedDouble },
  { id: "bed_double", name: "Bed (Double)", icon: BedDouble },
  { id: "bed_single", name: "Bed (Single)", icon: BedDouble },
  { id: "mattress", name: "Mattress Only", icon: BedDouble },
  { id: "dresser", name: "Dresser", icon: Package },
  { id: "nightstand", name: "Nightstand", icon: Package },
  { id: "dining_table", name: "Dining Table", icon: UtensilsCrossed },
  { id: "dining_chairs", name: "Dining Chairs", icon: UtensilsCrossed },
  { id: "refrigerator", name: "Refrigerator", icon: Refrigerator },
  { id: "washer", name: "Washer", icon: Package },
  { id: "dryer", name: "Dryer", icon: Package },
  { id: "dishwasher", name: "Dishwasher", icon: Package },
  { id: "tv_stand", name: "TV Stand", icon: Tv },
  { id: "bookshelf", name: "Bookshelf", icon: Package },
  { id: "desk", name: "Desk", icon: Package },
  { id: "office_chair", name: "Office Chair", icon: Package },
  { id: "wardrobe", name: "Wardrobe/Armoire", icon: Package },
];

const SPECIALTY_ITEMS = [
  { id: "piano_upright", name: "Piano (Upright)", icon: Piano },
  { id: "piano_grand", name: "Piano (Grand)", icon: Piano },
  { id: "pool_table", name: "Pool Table", icon: Package },
  { id: "safe_heavy", name: "Heavy Safe", icon: Package },
  { id: "hot_tub", name: "Hot Tub", icon: Package },
  { id: "gym_equipment", name: "Gym Equipment", icon: Dumbbell },
  { id: "antique_furniture", name: "Antique Furniture", icon: Package },
];

const BOX_ITEMS = [
  { id: "boxes_small", name: "Small Boxes", icon: Box },
  { id: "boxes_medium", name: "Medium Boxes", icon: Box },
  { id: "boxes_large", name: "Large Boxes", icon: Box },
];

const QuotePage = () => {
  const [step, setStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [quoteResult, setQuoteResult] = useState(null);
  const [isCalculatingDistance, setIsCalculatingDistance] = useState(false);
  const [pickupCoords, setPickupCoords] = useState(null);
  const [deliveryCoords, setDeliveryCoords] = useState(null);
  
  // Form data
  const [formData, setFormData] = useState({
    customer_name: "",
    customer_email: "",
    customer_phone: "",
    pickup_address: "",
    delivery_address: "",
    distance_km: 10,
    num_stops: 1,
    crew_size: "2_men",
    is_out_of_town: false,
    home_type: "2_bedroom",
    property_type_from: "apartment",
    property_type_to: "apartment",
    preferred_date: "",
    notes: "",
    other_items: ""
  });

  // Calculate distance when both addresses are set
  const calculateDistance = useCallback(async (origin, destination) => {
    if (!origin || !destination) return;
    
    setIsCalculatingDistance(true);
    
    try {
      const response = await axios.get(`${API}/distance`, {
        params: {
          origin_lat: origin.lat,
          origin_lng: origin.lng,
          dest_lat: destination.lat,
          dest_lng: destination.lng
        }
      });
      
      const data = response.data;
      setFormData(prev => ({ 
        ...prev, 
        distance_km: data.distance_km,
        is_out_of_town: data.is_out_of_town
      }));
      
      toast.success(`Distance: ${data.distance_km} km (${data.duration_text} drive)`);
    } catch (error) {
      console.error("Distance calculation error:", error);
      toast.error("Could not calculate distance. Please enter manually.");
    } finally {
      setIsCalculatingDistance(false);
    }
  }, []);

  // Effect to calculate distance when both coords are available
  useEffect(() => {
    if (pickupCoords && deliveryCoords) {
      calculateDistance(pickupCoords, deliveryCoords);
    }
  }, [pickupCoords, deliveryCoords, calculateDistance]);

  const handlePickupPlaceSelect = (place) => {
    setPickupCoords({ lat: place.lat, lng: place.lng });
  };

  const handleDeliveryPlaceSelect = (place) => {
    setDeliveryCoords({ lat: place.lat, lng: place.lng });
  };

  // Selected items with quantities
  const [selectedItems, setSelectedItems] = useState({});

  const updateQuantity = (itemId, delta, isSpecialty = false) => {
    setSelectedItems(prev => {
      const current = prev[itemId] || { quantity: 0, is_specialty: isSpecialty };
      const newQty = Math.max(0, current.quantity + delta);
      
      if (newQty === 0) {
        const { [itemId]: _, ...rest } = prev;
        return rest;
      }
      
      return {
        ...prev,
        [itemId]: { quantity: newQty, is_specialty: isSpecialty }
      };
    });
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    
    const items = Object.entries(selectedItems).map(([name, data]) => ({
      name,
      quantity: data.quantity,
      is_specialty: data.is_specialty
    }));

    const payload = {
      ...formData,
      items
    };

    try {
      const response = await axios.post(`${API}/quotes`, payload);
      setQuoteResult(response.data);
      setStep(4);
      toast.success("Quote generated successfully!");
    } catch (error) {
      console.error("Quote submission error:", error);
      toast.error("Failed to generate quote. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const totalItems = Object.values(selectedItems).reduce((sum, item) => sum + item.quantity, 0);
  const hasSpecialtyItems = Object.entries(selectedItems).some(([_, data]) => data.is_specialty && data.quantity > 0);

  const ItemCard = ({ item, isSpecialty = false }) => {
    const qty = selectedItems[item.id]?.quantity || 0;
    const Icon = item.icon;
    const [localValue, setLocalValue] = React.useState(qty.toString());
    const [isFocused, setIsFocused] = React.useState(false);
    
    // Sync local value with parent state when not focused
    React.useEffect(() => {
      if (!isFocused) {
        setLocalValue(qty.toString());
      }
    }, [qty, isFocused]);
    
    const handleQuantityChange = (e) => {
      // Just update local state while typing - don't update parent yet
      const value = e.target.value.replace(/[^0-9]/g, ''); // Only allow numbers
      setLocalValue(value);
    };
    
    const handleBlur = () => {
      setIsFocused(false);
      // Update parent state when user finishes typing
      const value = parseInt(localValue) || 0;
      const newQty = Math.max(0, Math.min(99, value));
      setLocalValue(newQty.toString());
      setSelectedItems(prev => ({
        ...prev,
        [item.id]: { quantity: newQty, is_specialty: isSpecialty }
      }));
    };
    
    const handleKeyDown = (e) => {
      // Update on Enter key
      if (e.key === 'Enter') {
        e.target.blur();
      }
    };
    
    return (
      <div 
        className={`border-2 rounded-lg p-4 transition-all ${
          qty > 0 
            ? "border-[#0047AB] bg-blue-50" 
            : "border-slate-200 hover:border-slate-300"
        }`}
        data-testid={`item-${item.id}`}
      >
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <Icon className={`w-5 h-5 ${qty > 0 ? "text-[#0047AB]" : "text-slate-400"}`} />
            <span className="font-medium text-sm">{item.name}</span>
          </div>
          {isSpecialty && (
            <span className="bg-slate-600 text-white text-xs font-bold px-2 py-0.5 rounded-full">
              Heavy
            </span>
          )}
        </div>
        <div className="flex items-center justify-center gap-2">
          <button
            type="button"
            onClick={() => updateQuantity(item.id, -1, isSpecialty)}
            className="w-8 h-8 rounded-full bg-slate-100 hover:bg-slate-200 flex items-center justify-center transition-colors"
            data-testid={`${item.id}-minus`}
          >
            <Minus className="w-4 h-4" />
          </button>
          <input
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            value={isFocused ? localValue : qty}
            onChange={handleQuantityChange}
            onFocus={() => setIsFocused(true)}
            onBlur={handleBlur}
            onKeyDown={handleKeyDown}
            className="w-12 h-8 text-center font-bold text-lg border border-slate-200 rounded-md focus:border-[#0047AB] focus:outline-none focus:ring-1 focus:ring-[#0047AB]"
            data-testid={`${item.id}-input`}
          />
          <button
            type="button"
            onClick={() => updateQuantity(item.id, 1, isSpecialty)}
            className="w-8 h-8 rounded-full bg-[#0047AB] hover:bg-[#003380] text-white flex items-center justify-center transition-colors"
            data-testid={`${item.id}-plus`}
          >
            <Plus className="w-4 h-4" />
          </button>
        </div>
      </div>
    );
  };

  return (
    <div data-testid="quote-page">
      {/* Hero Section with Video */}
      <VideoHero
        title="Free Moving Quote"
        subtitle="Get an instant estimate for your move. Fill in the details below."
        minHeight="min-h-[30vh]"
      />

      <div className="bg-[#F8FAFC] py-12">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Pricing Info Banner */}
        <div className="bg-[#0047AB] text-white rounded-xl p-6 mb-8">
          <div className="grid grid-cols-3 gap-4 text-center mb-4">
            <div className="bg-white/10 rounded-lg p-3">
              <p className="text-2xl font-bold font-['Barlow_Condensed']">$165/hr</p>
              <p className="text-white/80 text-sm">2 Men + Truck</p>
            </div>
            <div className="bg-white/10 rounded-lg p-3">
              <p className="text-2xl font-bold font-['Barlow_Condensed']">$190/hr</p>
              <p className="text-white/80 text-sm">3 Men + Truck</p>
            </div>
            <div className="bg-white/10 rounded-lg p-3">
              <p className="text-2xl font-bold font-['Barlow_Condensed']">$210/hr</p>
              <p className="text-white/80 text-sm">4 Men + Truck</p>
            </div>
          </div>
          <p className="text-center text-sm text-white/80 mb-3">2.5 Hour Minimum • Insurance & Vehicle Included</p>
          <div className="border-t border-white/20 pt-4">
            <p className="text-sm text-white/90 text-center mb-2 font-semibold">Surcharges:</p>
            <div className="flex flex-wrap justify-center gap-2 text-xs">
              <span className="bg-white/20 px-3 py-1 rounded-full">Out of Town: +$100</span>
              <span className="bg-white/20 px-3 py-1 rounded-full">Pool Table: +$400-600</span>
              <span className="bg-white/20 px-3 py-1 rounded-full">Piano (Upright): +$150</span>
              <span className="bg-white/20 px-3 py-1 rounded-full">Piano (Grand): +$300</span>
              <span className="bg-white/20 px-3 py-1 rounded-full">Hot Tub: +$250</span>
            </div>
          </div>
        </div>

        {/* Progress Steps */}
        <div className="flex items-center justify-center mb-10">
          {[1, 2, 3].map((s) => (
            <React.Fragment key={s}>
              <div 
                className={`w-10 h-10 rounded-full flex items-center justify-center font-bold ${
                  step >= s 
                    ? "bg-[#0047AB] text-white" 
                    : "bg-slate-200 text-slate-500"
                }`}
              >
                {step > s ? <CheckCircle className="w-5 h-5" /> : s}
              </div>
              {s < 3 && (
                <div className={`w-20 h-1 ${step > s ? "bg-[#0047AB]" : "bg-slate-200"}`}></div>
              )}
            </React.Fragment>
          ))}
        </div>

        {/* Step Content */}
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6 md:p-8">
          
          {/* Step 1: Move Details */}
          {step === 1 && (
            <div className="step-enter" data-testid="step-1">
              <h2 className="text-2xl font-bold font-['Barlow_Condensed'] text-slate-900 mb-6 flex items-center gap-2">
                <MapPin className="w-6 h-6 text-[#0047AB]" />
                Move Details
              </h2>

              {/* Home Size Selection */}
              <div className="mb-6">
                <Label className="flex items-center gap-2 mb-3">
                  <Home className="w-4 h-4" />
                  What size is your home?
                </Label>
                <div className="grid grid-cols-3 md:grid-cols-6 gap-2">
                  {HOME_SIZES.map((size) => (
                    <button
                      key={size.value}
                      type="button"
                      onClick={() => setFormData(prev => ({ ...prev, home_type: size.value }))}
                      className={`p-3 rounded-lg border-2 text-center transition-all ${
                        formData.home_type === size.value
                          ? "border-[#0047AB] bg-blue-50"
                          : "border-slate-200 hover:border-slate-300"
                      }`}
                      data-testid={`home-${size.value}`}
                    >
                      <p className="font-bold text-sm text-slate-900">{size.label}</p>
                      <p className="text-xs text-slate-500">{size.hours}</p>
                    </button>
                  ))}
                </div>
              </div>

              {/* Property Type FROM Selection */}
              <div className="mb-6">
                <Label className="flex items-center gap-2 mb-3">
                  <Building className="w-4 h-4" />
                  Moving FROM (Property Type)
                </Label>
                <div className="grid grid-cols-5 gap-2">
                  {PROPERTY_TYPES.map((type) => {
                    const Icon = type.icon;
                    return (
                      <button
                        key={type.value}
                        type="button"
                        onClick={() => setFormData(prev => ({ ...prev, property_type_from: type.value }))}
                        className={`p-3 rounded-lg border-2 text-center transition-all ${
                          formData.property_type_from === type.value
                            ? "border-[#0047AB] bg-blue-50"
                            : "border-slate-200 hover:border-slate-300"
                        }`}
                        data-testid={`property-from-${type.value}`}
                      >
                        <Icon className={`w-5 h-5 mx-auto mb-1 ${formData.property_type_from === type.value ? "text-[#0047AB]" : "text-slate-400"}`} />
                        <p className="font-medium text-xs text-slate-900">{type.label}</p>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Property Type TO Selection */}
              <div className="mb-6">
                <Label className="flex items-center gap-2 mb-3">
                  <Building className="w-4 h-4" />
                  Moving TO (Property Type)
                </Label>
                <div className="grid grid-cols-5 gap-2">
                  {PROPERTY_TYPES.map((type) => {
                    const Icon = type.icon;
                    return (
                      <button
                        key={type.value}
                        type="button"
                        onClick={() => setFormData(prev => ({ ...prev, property_type_to: type.value }))}
                        className={`p-3 rounded-lg border-2 text-center transition-all ${
                          formData.property_type_to === type.value
                            ? "border-[#0047AB] bg-blue-50"
                            : "border-slate-200 hover:border-slate-300"
                        }`}
                        data-testid={`property-to-${type.value}`}
                      >
                        <Icon className={`w-5 h-5 mx-auto mb-1 ${formData.property_type_to === type.value ? "text-[#0047AB]" : "text-slate-400"}`} />
                        <p className="font-medium text-xs text-slate-900">{type.label}</p>
                      </button>
                    );
                  })}
                </div>
              </div>
              
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <Label htmlFor="pickup_address">Pickup Address *</Label>
                  <div className="mt-2">
                    <AddressAutocomplete
                      id="pickup_address"
                      value={formData.pickup_address}
                      onChange={handleInputChange}
                      onPlaceSelect={handlePickupPlaceSelect}
                      placeholder="Start typing address..."
                      required
                      data-testid="pickup-address-input"
                    />
                  </div>
                </div>
                <div>
                  <Label htmlFor="delivery_address">Delivery Address *</Label>
                  <div className="mt-2">
                    <AddressAutocomplete
                      id="delivery_address"
                      value={formData.delivery_address}
                      onChange={handleInputChange}
                      onPlaceSelect={handleDeliveryPlaceSelect}
                      placeholder="Start typing address..."
                      required
                      data-testid="delivery-address-input"
                    />
                  </div>
                </div>
              </div>

              {/* Distance Display */}
              <div className="mt-6">
                <div className="flex items-center justify-between mb-2">
                  <Label className="flex items-center gap-2">
                    <Navigation className="w-4 h-4" />
                    Distance: {formData.distance_km} km
                  </Label>
                  {isCalculatingDistance && (
                    <span className="flex items-center gap-2 text-sm text-[#0047AB]">
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Calculating...
                    </span>
                  )}
                </div>
                <Slider
                  value={[formData.distance_km]}
                  onValueChange={(val) => setFormData(prev => ({ ...prev, distance_km: val[0] }))}
                  min={1}
                  max={200}
                  step={1}
                  className="mt-2"
                  data-testid="distance-slider"
                />
                <div className="flex justify-between text-sm text-slate-500 mt-1">
                  <span>1 km</span>
                  <span>200 km</span>
                </div>
              </div>

              <div className="mt-6">
                <Label>Number of Stops/Pickups: {formData.num_stops}</Label>
                <Slider
                  value={[formData.num_stops]}
                  onValueChange={(val) => setFormData(prev => ({ ...prev, num_stops: val[0] }))}
                  min={1}
                  max={5}
                  step={1}
                  className="mt-4"
                  data-testid="stops-slider"
                />
                <div className="flex justify-between text-sm text-slate-500 mt-1">
                  <span>1 stop</span>
                  <span>5 stops</span>
                </div>
              </div>

              {/* Crew Size Selection */}
              <div className="mt-6">
                <Label className="flex items-center gap-2 mb-3">
                  <Users className="w-4 h-4" />
                  Crew Size
                </Label>
                <div className="grid grid-cols-3 gap-3">
                  {[
                    { value: "2_men", label: "2 Men", price: "$165/hr" },
                    { value: "3_men", label: "3 Men", price: "$190/hr" },
                    { value: "4_men", label: "4 Men", price: "$210/hr" },
                  ].map((option) => (
                    <button
                      key={option.value}
                      type="button"
                      onClick={() => setFormData(prev => ({ ...prev, crew_size: option.value }))}
                      className={`p-4 rounded-lg border-2 text-center transition-all ${
                        formData.crew_size === option.value
                          ? "border-[#0047AB] bg-blue-50"
                          : "border-slate-200 hover:border-slate-300"
                      }`}
                      data-testid={`crew-${option.value}`}
                    >
                      <p className="font-bold text-slate-900">{option.label}</p>
                      <p className="text-sm text-[#0047AB] font-semibold">{option.price}</p>
                    </button>
                  ))}
                </div>
              </div>

              {/* Out of Town Checkbox */}
              <div className="mt-6 flex items-center space-x-3 p-4 bg-slate-50 rounded-lg">
                <Checkbox
                  id="is_out_of_town"
                  checked={formData.is_out_of_town}
                  onCheckedChange={(checked) => setFormData(prev => ({ ...prev, is_out_of_town: checked }))}
                  data-testid="out-of-town-checkbox"
                />
                <div>
                  <Label htmlFor="is_out_of_town" className="font-semibold cursor-pointer">
                    Out of Town Move (+$100)
                  </Label>
                  <p className="text-sm text-slate-500">Check if moving outside Cornwall/SDG area (covers gas & km)</p>
                </div>
              </div>

              <div className="mt-6">
                <Label htmlFor="preferred_date">Preferred Move Date (Optional)</Label>
                <Input
                  id="preferred_date"
                  name="preferred_date"
                  type="date"
                  value={formData.preferred_date}
                  onChange={handleInputChange}
                  className="mt-2"
                  data-testid="preferred-date-input"
                />
              </div>

              <div className="flex justify-end mt-8">
                <Button 
                  onClick={() => setStep(2)}
                  disabled={!formData.pickup_address || !formData.delivery_address}
                  className="bg-[#0047AB] hover:bg-[#003380] text-white font-bold"
                  data-testid="next-step-1"
                >
                  Next: Select Items
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </div>
          )}

          {/* Step 2: Items Selection */}
          {step === 2 && (
            <div className="step-enter" data-testid="step-2">
              <h2 className="text-2xl font-bold font-['Barlow_Condensed'] text-slate-900 mb-6 flex items-center gap-2">
                <Box className="w-6 h-6 text-[#0047AB]" />
                What Are You Moving?
              </h2>

              {/* Regular Items */}
              <div className="mb-8">
                <h3 className="text-lg font-semibold text-slate-700 mb-4">Furniture & Appliances</h3>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                  {REGULAR_ITEMS.map(item => (
                    <ItemCard key={item.id} item={item} />
                  ))}
                </div>
              </div>

              {/* Boxes */}
              <div className="mb-8">
                <h3 className="text-lg font-semibold text-slate-700 mb-4">Boxes</h3>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {BOX_ITEMS.map(item => (
                    <ItemCard key={item.id} item={item} />
                  ))}
                </div>
              </div>

              {/* Specialty Items */}
              <div className="mb-8">
                <h3 className="text-lg font-semibold text-slate-700 mb-4 flex items-center gap-2">
                  Specialty / Heavy Items
                  <span className="bg-slate-600 text-white text-xs font-bold px-2 py-0.5 rounded-full">
                    Extra Care Required
                  </span>
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                  {SPECIALTY_ITEMS.map(item => (
                    <ItemCard key={item.id} item={item} isSpecialty={true} />
                  ))}
                </div>
              </div>

              {/* Other Items */}
              <div className="mb-6">
                <Label htmlFor="other_items">Other Items (describe anything not listed above)</Label>
                <Textarea
                  id="other_items"
                  name="other_items"
                  placeholder="E.g., 2 large mirrors, outdoor patio set, garden tools..."
                  value={formData.other_items}
                  onChange={handleInputChange}
                  className="mt-2"
                  rows={3}
                  data-testid="other-items-input"
                />
              </div>

              {/* Summary */}
              <div className="bg-slate-50 rounded-lg p-4 mb-6">
                <p className="text-slate-700">
                  <strong>Items Selected:</strong> {totalItems}
                  {hasSpecialtyItems && (
                    <span className="ml-2 text-slate-600 font-semibold">
                      (includes specialty items)
                    </span>
                  )}
                </p>
              </div>

              <div className="flex justify-between mt-8">
                <Button 
                  variant="outline"
                  onClick={() => setStep(1)}
                  className="font-bold"
                  data-testid="back-step-2"
                >
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Back
                </Button>
                <Button 
                  onClick={() => setStep(3)}
                  className="bg-[#0047AB] hover:bg-[#003380] text-white font-bold"
                  data-testid="next-step-2"
                >
                  Next: Your Info
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </div>
          )}

          {/* Step 3: Contact Info */}
          {step === 3 && (
            <div className="step-enter" data-testid="step-3">
              <h2 className="text-2xl font-bold font-['Barlow_Condensed'] text-slate-900 mb-6 flex items-center gap-2">
                <Truck className="w-6 h-6 text-[#0047AB]" />
                Your Contact Information
              </h2>

              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <Label htmlFor="customer_name">Full Name *</Label>
                  <Input
                    id="customer_name"
                    name="customer_name"
                    placeholder="John Smith"
                    value={formData.customer_name}
                    onChange={handleInputChange}
                    className="mt-2"
                    data-testid="customer-name-input"
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="customer_phone">Phone Number *</Label>
                  <Input
                    id="customer_phone"
                    name="customer_phone"
                    type="tel"
                    placeholder="613-555-0123"
                    value={formData.customer_phone}
                    onChange={handleInputChange}
                    className="mt-2"
                    data-testid="customer-phone-input"
                    required
                  />
                </div>
              </div>

              <div className="mt-6">
                <Label htmlFor="customer_email">Email Address *</Label>
                <Input
                  id="customer_email"
                  name="customer_email"
                  type="email"
                  placeholder="john@example.com"
                  value={formData.customer_email}
                  onChange={handleInputChange}
                  className="mt-2"
                  data-testid="customer-email-input"
                  required
                />
              </div>

              <div className="mt-6">
                <Label htmlFor="notes">Additional Notes (Optional)</Label>
                <Textarea
                  id="notes"
                  name="notes"
                  placeholder="Anything else we should know about your move..."
                  value={formData.notes}
                  onChange={handleInputChange}
                  className="mt-2"
                  rows={3}
                  data-testid="notes-input"
                />
              </div>

              <div className="flex justify-between mt-8">
                <Button 
                  variant="outline"
                  onClick={() => setStep(2)}
                  className="font-bold"
                  data-testid="back-step-3"
                >
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Back
                </Button>
                <Button 
                  onClick={handleSubmit}
                  disabled={!formData.customer_name || !formData.customer_email || !formData.customer_phone || isSubmitting}
                  className="bg-[#0047AB] hover:bg-[#003380] text-white font-bold"
                  data-testid="submit-quote"
                >
                  {isSubmitting ? "Generating Quote..." : "Get My Quote"}
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </div>
          )}

          {/* Step 4: Quote Result */}
          {step === 4 && quoteResult && (
            <div className="step-enter" data-testid="step-4">
              <div className="text-center">
                <div className="bg-green-50 border border-green-200 rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-6">
                  <CheckCircle className="w-10 h-10 text-green-600" />
                </div>
                
                <h2 className="text-3xl font-bold font-['Barlow_Condensed'] text-slate-900 mb-4">
                  Your Quote Estimate
                </h2>
              </div>
              
              {/* Total Price */}
              <div className="bg-[#0047AB] text-white rounded-2xl p-8 mb-6 text-center">
                <p className="text-lg mb-2">Estimated Total</p>
                <p className="text-5xl font-bold font-['Barlow_Condensed']" data-testid="quote-total">
                  ${quoteResult.total_estimate.toFixed(2)} CAD
                </p>
              </div>

              {/* Price Breakdown */}
              <div className="bg-white border border-slate-200 rounded-xl p-6 mb-6">
                <h3 className="font-bold text-lg mb-4 text-slate-900">Price Breakdown:</h3>
                <div className="space-y-3">
                  <div className="flex justify-between items-center py-2 border-b border-slate-100">
                    <span className="text-slate-600">Estimated Time</span>
                    <span className="font-bold text-slate-900">{quoteResult.estimated_hours} hours</span>
                  </div>
                  <div className="flex justify-between items-center py-2 border-b border-slate-100">
                    <span className="text-slate-600">Hourly Rate ({quoteResult.crew_size?.replace("_", " ").replace("men", "Men")})</span>
                    <span className="font-bold text-slate-900">${quoteResult.hourly_rate}/hour</span>
                  </div>
                  <div className="flex justify-between items-center py-2 border-b border-slate-100">
                    <span className="text-slate-600">Labor Cost ({quoteResult.estimated_hours} hrs × ${quoteResult.hourly_rate})</span>
                    <span className="font-bold text-slate-900">${(quoteResult.estimated_hours * quoteResult.hourly_rate).toFixed(2)}</span>
                  </div>
                  {quoteResult.out_of_town_surcharge > 0 && (
                    <div className="flex justify-between items-center py-2 border-b border-slate-100">
                      <span className="text-slate-600">Out of Town Surcharge (gas + km)</span>
                      <span className="font-bold text-slate-900">+${quoteResult.out_of_town_surcharge.toFixed(2)}</span>
                    </div>
                  )}
                  {quoteResult.specialty_surcharge > 0 && (
                    <div className="flex justify-between items-center py-2 border-b border-slate-100">
                      <span className="text-slate-600">Specialty Item Surcharges</span>
                      <span className="font-bold text-slate-900">+${quoteResult.specialty_surcharge.toFixed(2)}</span>
                    </div>
                  )}
                  {quoteResult.breakdown?.specialty_items?.length > 0 && (
                    <div className="pl-4 space-y-1">
                      {quoteResult.breakdown.specialty_items.map((item, idx) => (
                        <div key={idx} className="flex justify-between text-sm text-slate-500">
                          <span>• {item.name} (×{item.quantity})</span>
                          <span>+${item.fee.toFixed(2)}</span>
                        </div>
                      ))}
                    </div>
                  )}
                  <div className="flex justify-between items-center py-3 bg-slate-50 rounded-lg px-3 mt-2">
                    <span className="font-bold text-slate-900">Total Estimate</span>
                    <span className="font-bold text-xl text-[#0047AB]">${quoteResult.total_estimate.toFixed(2)} CAD</span>
                  </div>
                </div>
              </div>

              {/* Move Details */}
              <div className="bg-slate-50 rounded-xl p-6 mb-6">
                <h3 className="font-bold text-lg mb-4 text-slate-900">Move Details:</h3>
                <div className="grid md:grid-cols-2 gap-4 text-slate-700">
                  <div>
                    <p className="text-sm text-slate-500">Home Size</p>
                    <p className="font-medium">{quoteResult.home_type?.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Move Type</p>
                    <p className="font-medium">{quoteResult.property_type_from?.charAt(0).toUpperCase() + quoteResult.property_type_from?.slice(1)} → {quoteResult.property_type_to?.charAt(0).toUpperCase() + quoteResult.property_type_to?.slice(1)}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Crew Size</p>
                    <p className="font-medium">{quoteResult.crew_size?.replace('_', ' ').replace('men', 'Men')}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Distance</p>
                    <p className="font-medium">{quoteResult.distance_km} km</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">From</p>
                    <p className="font-medium">{quoteResult.pickup_address}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">To</p>
                    <p className="font-medium">{quoteResult.delivery_address}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Stops</p>
                    <p className="font-medium">{quoteResult.num_stops}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Items</p>
                    <p className="font-medium">{quoteResult.breakdown?.regular_items_count || 0} regular items</p>
                  </div>
                  {quoteResult.other_items && (
                    <div className="md:col-span-2">
                      <p className="text-sm text-slate-500">Other Items</p>
                      <p className="font-medium">{quoteResult.other_items}</p>
                    </div>
                  )}
                </div>
              </div>

              <p className="text-slate-600 mb-6 text-center">
                This is an estimate. For an exact quote and to book your move, 
                call Tony directly at <a href="tel:613-362-8180" className="text-[#0047AB] font-bold">613-362-8180</a>
              </p>

              <p className="text-green-600 text-sm mb-6 text-center font-medium">
                ✓ A copy of this quote has been sent to your email!
              </p>

              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button 
                  onClick={() => {
                    setStep(1);
                    setQuoteResult(null);
                    setSelectedItems({});
                    setPickupCoords(null);
                    setDeliveryCoords(null);
                    setFormData({
                      customer_name: "",
                      customer_email: "",
                      customer_phone: "",
                      pickup_address: "",
                      delivery_address: "",
                      distance_km: 10,
                      num_stops: 1,
                      crew_size: "2_men",
                      is_out_of_town: false,
                      home_type: "2_bedroom",
                      property_type_from: "apartment",
                      property_type_to: "apartment",
                      preferred_date: "",
                      notes: "",
                      other_items: ""
                    });
                  }}
                  variant="outline"
                  className="font-bold"
                  data-testid="new-quote-btn"
                >
                  Start New Quote
                </Button>
                <a href="/booking">
                  <Button className="bg-slate-600 hover:bg-slate-700 text-white font-bold" data-testid="book-now-btn">
                    Book Your Move Now
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </a>
              </div>
            </div>
          )}
        </div>
      </div>
      </div>
    </div>
  );
};

export default QuotePage;
